import React, { useState, useEffect, useRef } from 'react'
import { videoAPI } from '../../services/api'

const createInitialUploadForm = () => ({
  name: '',
  description: '',
  category: 'general',
  title: '',
  creator: '',
  source: '',
  tags: '',
  isLoop: false,
  file: null,
  detectedAttributes: null,
  videoUrl: ''
})

const VideoTab = () => {
  const [videos, setVideos] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadMode, setUploadMode] = useState('file')
  const [uploadForm, setUploadForm] = useState(() => createInitialUploadForm())
  const videoRefs = useRef({})
  const [toast, setToast] = useState('')
  const [previewVideo, setPreviewVideo] = useState(null)

  useEffect(() => {
    fetchVideos()
    fetchCategories()
  }, [selectedCategory, searchTerm])

  const fetchVideos = async () => {
    try {
      setLoading(true)
      const params = {
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        search: searchTerm || undefined,
        limit: 50
      }
      
      const response = await videoAPI.getAll(params)
      if (response.data.success) {
        setVideos(response.data.data.videos || [])
      }
    } catch (error) {
      console.error('Error fetching videos:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await videoAPI.getCategories()
      if (response.data.success) {
        setCategories(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const handleCopyUrl = async (video) => {
    try {
      const absoluteUrl = `${window.location.origin}/api/v1/videos/${video.id}/stream`
      await navigator.clipboard.writeText(absoluteUrl)
      setToast('Video URL copied')
      setTimeout(() => setToast(''), 2000)
    } catch (e) {
      console.error('Copy URL failed', e)
    }
  }

  const handlePlay = (videoId) => {
    // Stop currently playing video
    if (currentlyPlaying && videoRefs.current[currentlyPlaying]) {
      videoRefs.current[currentlyPlaying].pause()
      videoRefs.current[currentlyPlaying].currentTime = 0
    }

    if (currentlyPlaying === videoId) {
      setCurrentlyPlaying(null)
    } else {
      setCurrentlyPlaying(videoId)
      if (videoRefs.current[videoId]) {
        videoRefs.current[videoId].play()
      }
    }
  }

  const handleVideoEnd = (videoId) => {
    if (currentlyPlaying === videoId) {
      setCurrentlyPlaying(null)
    }
  }

  const resetUploadForm = () => {
    setUploadForm(createInitialUploadForm())
  }

  const handleUploadModeChange = (mode) => {
    if (mode === uploadMode) return
    setUploadMode(mode)
  }

  const handleOpenUploadModal = (mode = 'file') => {
    handleUploadModeChange(mode)
    setShowUploadModal(true)
  }

  const handleCloseUploadModal = () => {
    setShowUploadModal(false)
    setUploadMode('file')
    resetUploadForm()
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    const currentMode = uploadMode

    if (currentMode === 'file' && !uploadForm.file) {
      alert('Please select a video file')
      return
    }

    if (currentMode === 'url') {
      const urlValue = uploadForm.videoUrl?.trim()
      if (!urlValue) {
        alert('Please enter a video URL')
        return
      }
      try {
        const parsed = new URL(urlValue)
        if (!parsed.protocol.startsWith('http')) {
          alert('Invalid URL. Must start with http:// or https://')
          return
        }
      } catch (err) {
        alert('Invalid URL format')
        return
      }
    }

    try {
      setUploading(true)
      const formData = new FormData()
      if (currentMode === 'file') {
        formData.append('videoFile', uploadForm.file)
      } else {
        formData.append('videoUrl', uploadForm.videoUrl.trim())
      }

      if (uploadForm.name) formData.append('name', uploadForm.name)
      if (uploadForm.description) formData.append('description', uploadForm.description)
      if (uploadForm.category) formData.append('category', uploadForm.category)
      if (uploadForm.title) formData.append('title', uploadForm.title)
      if (uploadForm.creator) formData.append('creator', uploadForm.creator)
      if (uploadForm.source) formData.append('source', uploadForm.source)
      if (uploadForm.tags) formData.append('tags', uploadForm.tags)
      formData.append('isLoop', uploadForm.isLoop)

      const response = currentMode === 'file'
        ? await videoAPI.upload(formData)
        : await videoAPI.importFromUrl(formData)

      if (response.data.success) {
        handleCloseUploadModal()
        fetchVideos()
        setSuccessMessage(currentMode === 'file' ? 'Video uploaded successfully!' : 'Video imported successfully!')
        setShowSuccess(true)
      }
    } catch (error) {
      console.error(currentMode === 'file' ? 'Error uploading video:' : 'Error importing video:', error)
      alert(currentMode === 'file' ? 'Failed to upload video' : 'Failed to import video from URL')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return
    }

    try {
      await videoAPI.delete(videoId)
      fetchVideos()
      alert('Video deleted successfully!')
    } catch (error) {
      console.error('Error deleting video:', error)
      alert('Failed to delete video')
    }
  }

  const handleDownload = async (videoId, filename) => {
    try {
      const response = await videoAPI.download(videoId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading video:', error)
      alert('Failed to download video')
    }
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown'
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(1)} MB`
  }

  const getResolutionLabel = (width, height) => {
    if (!width || !height) return 'Unknown'
    if (height >= 2160) return '4K'
    if (height >= 1080) return 'Full HD'
    if (height >= 720) return 'HD'
    return `${width}x${height}`
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Video Library</h2>
          <p className="text-gray-600">Browse and manage your video files</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => handleOpenUploadModal('file')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            <span>Upload Video</span>
          </button>
          <button
            onClick={() => handleOpenUploadModal('url')}
            className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.59 13.41a1.5 1.5 0 010-2.12l2.83-2.83a1.5 1.5 0 012.12 0l.59.59a4 4 0 010 5.66l-2.83 2.83a4 4 0 01-5.66 0l-.59-.59" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.41 10.59a1.5 1.5 0 010 2.12l-2.83 2.83a1.5 1.5 0 01-2.12 0l-.59-.59a4 4 0 010-5.66l2.83-2.83a4 4 0 015.66 0l.59.59" />
            </svg>
            <span>Upload from URL</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex space-x-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search video files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.slug}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Video Grid */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : videos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((video) => (
              <div key={video.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Video Preview */}
                <div className="relative aspect-video bg-gray-900">
          <video
                    ref={(el) => {
                      if (el) videoRefs.current[video.id] = el
                    }}
                    onEnded={() => handleVideoEnd(video.id)}
                    className="w-full h-full object-cover"
                    preload="metadata"
            controls
                  >
            <source src={`/api/v1/videos/${video.id}/stream`} type={video.mimeType} />
                  </video>
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <button
                        onClick={() => handleDownload(video.id, video.originalFilename)}
                        className="p-2 rounded-md bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700"
                        title="Download"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                      </button>
                      <button
                        onClick={() => handleCopyUrl(video)}
                        className="p-2 rounded-md bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700"
                        title="Copy URL"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 2a2 2 0 00-2 2v8a2 2 0 002 2h2v2H8a4 4 0 01-4-4V4a4 4 0 014-4h6a4 4 0 014 4v2h-2V4a2 2 0 00-2-2H8zm4 6a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2v-6a2 2 0 00-2-2h-6zm6 2h-6v6h6V10z" clipRule="evenodd"/></svg>
                      </button>
                      <button
                        onClick={() => setPreviewVideo(video)}
                        className="p-2 rounded-md bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700"
                        title="Fullscreen"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h4a1 1 0 110 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 11-2 0V4zm10-1a1 1 0 100 2h1.586l-2.293 2.293a1 1 0 001.414 1.414L16 6.414V8a1 1 0 102 0V4a1 1 0 00-1-1h-4zM4 13a1 1 0 112 0v1.586l2.293-2.293a1 1 0 111.414 1.414L7.414 16H9a1 1 0 110 2H5a1 1 0 01-1-1v-4zm13 0a1 1 0 00-1-1h-1.586l-2.293 2.293a1 1 0 101.414 1.414L15 14.414V16a1 1 0 102 0v-3z"/></svg>
                      </button>
                      <button
                        onClick={() => handleDelete(video.id)}
                        className="p-2 rounded-md bg-white bg-opacity-90 hover:bg-opacity-100 text-red-600"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd"/><path fillRule="evenodd" d="M10 5a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2V7a2 2 0 00-2-2H10z" clipRule="evenodd"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* Video Info Overlay */}
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="flex justify-between items-end">
                      <div className="text-white text-sm">
                        {video.duration && (
                          <span className="bg-black bg-opacity-50 px-2 py-1 rounded">
                            {formatDuration(video.duration)}
                          </span>
                        )}
                      </div>
                      <div className="text-white text-sm">
                        {video.width && video.height && (
                          <span className="bg-black bg-opacity-50 px-2 py-1 rounded">
                            {getResolutionLabel(video.width, video.height)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Video Details */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 truncate mb-1">{video.name}</h3>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                    <span className="capitalize">{video.category}</span>
                    <span>{formatFileSize(video.fileSize)}</span>
                  </div>
                  {video.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{video.description}</p>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDownload(video.id, video.originalFilename)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Download"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(video.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M10 5a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2V7a2 2 0 00-2-2H10z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    {video.isLoop && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        Loop
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No video files found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || selectedCategory !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Upload your first video file to get started.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => handleOpenUploadModal('file')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Upload Video
              </button>
              <button
                onClick={() => handleOpenUploadModal('url')}
                className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Upload from URL
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {uploadMode === 'file' ? 'Upload Video' : 'Import Video from URL'}
                </h3>
                <button
                  onClick={handleCloseUploadModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="flex border-b border-gray-200 mb-6">
                <button
                  type="button"
                  onClick={() => handleUploadModeChange('file')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    uploadMode === 'file'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => handleUploadModeChange('url')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    uploadMode === 'url'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Import from URL
                </button>
              </div>

              <form onSubmit={handleUpload} className="space-y-4">
                {uploadMode === 'file' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video File *
                    </label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (!file) return

                        // Check file size (200MB limit)
                        const maxSize = 200 * 1024 * 1024 // 200MB in bytes
                        if (file.size > maxSize) {
                          alert(`File size too large. Maximum allowed size is 200MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`)
                          e.target.value = '' // Clear the input
                          return
                        }

                        const name = file.name.replace(/\.[^/.]+$/, '')

                        // Extract video attributes using HTML5 video element
                        const video = document.createElement('video')
                        video.preload = 'metadata'

                        video.onloadedmetadata = () => {
                          console.log('dY"1 Video metadata loaded:', {
                            duration: video.duration,
                            width: video.videoWidth,
                            height: video.videoHeight
                          })

                          setUploadForm(prev => ({
                            ...prev,
                            file,
                            name: prev.name || name,
                            // Store detected attributes for display
                            detectedAttributes: {
                              duration: video.duration,
                              width: video.videoWidth,
                              height: video.videoHeight,
                              size: file.size,
                              type: file.type
                            }
                          }))

                          // Clean up
                          URL.revokeObjectURL(video.src)
                        }

                        video.onerror = () => {
                          console.warn('?s??,? Could not load video metadata')
                          setUploadForm(prev => ({
                            ...prev,
                            file,
                            name: prev.name || name,
                            detectedAttributes: {
                              size: file.size,
                              type: file.type
                            }
                          }))
                          URL.revokeObjectURL(video.src)
                        }

                        video.src = URL.createObjectURL(file)
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={uploadMode === 'file'}
                    />

                    {/* Display detected video attributes */}
                    {uploadForm.detectedAttributes && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
                        <p><strong>File Info:</strong></p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Size: {(uploadForm.detectedAttributes.size / (1024 * 1024)).toFixed(1)} MB</li>
                          <li>Type: {uploadForm.detectedAttributes.type}</li>
                          {uploadForm.detectedAttributes.duration && (
                            <li>Duration: {uploadForm.detectedAttributes.duration.toFixed(1)} seconds</li>
                          )}
                          {uploadForm.detectedAttributes.width && uploadForm.detectedAttributes.height && (
                            <li>Resolution: {uploadForm.detectedAttributes.width} A- {uploadForm.detectedAttributes.height}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video URL *
                    </label>
                    <input
                      type="url"
                      value={uploadForm.videoUrl}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, videoUrl: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com/video.mp4"
                      required={uploadMode === 'url'}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Provide a direct http(s) link to a video (up to 200MB). We'll download it, generate a thumbnail,
                      and push both the video and metadata to your library automatically.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={uploadForm.name}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Video name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={uploadForm.category}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="general">General</option>
                      <option value="stock">Stock Video</option>
                      <option value="animation">Animation</option>
                      <option value="background">Background</option>
                      <option value="tutorial">Tutorial</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Video description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Video title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Creator
                    </label>
                    <input
                      type="text"
                      value={uploadForm.creator}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, creator: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Creator name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Source
                    </label>
                    <input
                      type="text"
                      value={uploadForm.source}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, source: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Video source"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={uploadForm.tags}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Comma-separated tags"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isLoop"
                    checked={uploadForm.isLoop}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, isLoop: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isLoop" className="ml-2 block text-sm text-gray-900">
                    This video is designed to loop seamlessly
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-6">
                  <button
                    type="button"
                    onClick={handleCloseUploadModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      uploading ||
                      (uploadMode === 'file' && !uploadForm.file) ||
                      (uploadMode === 'url' && !uploadForm.videoUrl?.trim())
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {uploading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    <span>
                      {uploading
                        ? (uploadMode === 'file' ? 'Uploading...' : 'Importing...')
                        : (uploadMode === 'file' ? 'Upload Video' : 'Import Video')}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 rounded-full p-2">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293A1 1 0 106.293 10.707l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Success</h3>
                  <p className="text-sm opacity-90">{successMessage}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <button
                onClick={() => setShowSuccess(false)}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-5xl w-full">
            <button
              onClick={() => setPreviewVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
              title="Close"
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </button>
            <video className="w-full h-auto max-h-[80vh]" controls>
              <source src={`/api/v1/videos/${previewVideo.id}/stream`} type={previewVideo.mimeType} />
            </video>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-full shadow-lg">
          {toast}
        </div>
      )}
    </div>
  )
}

export default VideoTab
